/**
 * Every language the apps carry translations for.
 *
 * Adding one is a `locales/<code>/` folder beside each existing one (here and in each app), an entry
 * here, and its row in `LANGUAGE_IDS`. The key-parity tests then fail until every file has every key.
 */
export const SUPPORTED_LOCALES = ['en', 'it'] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

/** What a missing key falls back to, and what an unreadable or unsupported device gets. */
export const DEFAULT_LOCALE: Locale = 'en';

/**
 * The languages a user can end up in: offered in the picker, and picked from the device. Narrower
 * than `SUPPORTED_LOCALES` only while a language's files exist but are not ready to ship —
 * releasing one is adding it here.
 */
export const RELEASED_LOCALES: readonly Locale[] = ['en', 'it'];

/**
 * What each language is called in the picker: its own name for itself, the way every language
 * picker writes it, so a user who cannot read the current language can still find theirs.
 */
export const LOCALE_NAMES = {
  en: 'English',
  it: 'Italiano',
} as const satisfies Record<Locale, string>;

export const isSupportedLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);

/**
 * `languages.id` on the backend, which is what `users.language_id` — the user's stored choice —
 * holds. The table has ten rows; only these have translations behind them.
 */
export const LANGUAGE_IDS = {
  en: 1,
  it: 2,
} as const satisfies Record<Locale, number>;

/**
 * The locale a stored `language_id` names, or `null` for none or one without translations.
 *
 * Takes a string as well because `SettingsResource` types the id as one: production keeps it in a
 * blank-padded text column.
 */
export const localeFromLanguageId = (languageId: number | string | null | undefined): Locale | null => {
  if (languageId === null || languageId === undefined) {
    return null;
  }

  const id = typeof languageId === 'number' ? languageId : Number.parseInt(languageId.trim(), 10);

  for (const locale of SUPPORTED_LOCALES) {
    if (LANGUAGE_IDS[locale] === id) {
      return locale;
    }
  }

  return null;
};
